import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { MapContainer, TileLayer, Circle, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { toast } from "sonner";
import { Mail, MapPin, Clock, Loader2, CalendarClock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Reveal from "@/components/Reveal";
import { sendEnquiry } from "@/lib/api";

const markerIcon = new L.Icon({
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

const spots = [
  { name: "Noosa Heads", pos: [-26.3959, 153.0919] },
  { name: "Maroochydore", pos: [-26.6580, 153.0920] },
  { name: "Caloundra", pos: [-26.8030, 153.1290] },
];

const eventTypes = ["Beachfront Wedding", "Backyard Party", "Boat Party", "Corporate Event", "Other Celebration"];

const peakInfo = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T00:00:00");
  if (isNaN(d)) return null;
  const day = d.getDay();
  const month = d.getMonth();
  if (day === 5 || day === 6) return "Weekend date — these fill fast. Enquire early to lock it in.";
  if ([11, 0, 1].includes(month)) return "Peak summer season — dates are filling fast.";
  return null;
};

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", event_type: "", event_date: "", guest_count: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [prefillNote, setPrefillNote] = useState("");
  const location = useLocation();

  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const pkg = p.get("package");
    const guests = p.get("guests");
    const suburb = p.get("suburb");
    const total = p.get("total");
    if (!pkg && !guests) return;
    const pkgName = pkg ? pkg.charAt(0).toUpperCase() + pkg.slice(1) : "";
    const parts = [];
    if (pkgName) parts.push(`the ${pkgName} package`);
    if (guests) parts.push(`for ${guests} guests`);
    if (suburb && suburb !== "Custom distance") parts.push(`in ${suburb}`);
    if (total) parts.push(`(estimated $${Number(total).toLocaleString()})`);
    const msg = `Hi! I'd like to book ${parts.join(" ")}. Please confirm availability.`;
    setForm((f) => ({ ...f, guest_count: guests || f.guest_count, message: msg }));
    setPrefillNote(`Pre-filled from your ${pkgName} quote${total ? ` — ~$${Number(total).toLocaleString()}` : ""}`);
  }, [location.search]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.event_type) {
      toast.error("Please add your name, email and event type.");
      return;
    }
    setLoading(true);
    try {
      await sendEnquiry({ ...form, guest_count: parseInt(form.guest_count) || 0 });
      toast.success("Enquiry sent! Check your inbox for a confirmation.");
      setForm({ name: "", email: "", phone: "", event_type: "", event_date: "", guest_count: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-cream py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal className="max-w-2xl mb-16">
          <p className="overline text-xs text-coral mb-5">Enquire</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ocean-deep leading-[1.02] tracking-tight">
            Let&apos;s plan your coastal event.
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <Reveal>
            {prefillNote && (
              <div data-testid="prefill-note" className="mb-4 rounded-2xl bg-coral/10 border border-coral/30 px-4 py-3 text-xs overline text-coral flex items-center gap-2">
                <CalendarClock className="w-4 h-4" /> {prefillNote}
              </div>
            )}
            <form onSubmit={submit} data-testid="enquiry-form" className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Input data-testid="input-name" placeholder="Your name" value={form.name} onChange={upd("name")} className="h-12 bg-white border-sand rounded-xl" />
                <Input data-testid="input-email" type="email" placeholder="Email address" value={form.email} onChange={upd("email")} className="h-12 bg-white border-sand rounded-xl" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Input data-testid="input-phone" placeholder="Phone (optional)" value={form.phone} onChange={upd("phone")} className="h-12 bg-white border-sand rounded-xl" />
                <Select value={form.event_type} onValueChange={upd("event_type")}>
                  <SelectTrigger data-testid="select-event-type" className="h-12 bg-white border-sand rounded-xl">
                    <SelectValue placeholder="Event type" />
                  </SelectTrigger>
                  <SelectContent>
                    {eventTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <Input data-testid="input-date" type="date" value={form.event_date} onChange={upd("event_date")} className="h-12 bg-white border-sand rounded-xl text-ocean" />
                  {peakInfo(form.event_date) && (
                    <p data-testid="peak-date-flag" className="text-xs text-coral mt-2 flex items-start gap-1.5">
                      <CalendarClock className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {peakInfo(form.event_date)}
                    </p>
                  )}
                </div>
                <Input data-testid="input-guests" type="number" min="1" placeholder="Guest count" value={form.guest_count} onChange={upd("guest_count")} className="h-12 bg-white border-sand rounded-xl" />
              </div>
              <Textarea data-testid="input-message" placeholder="Tell us about your event..." value={form.message} onChange={upd("message")} className="min-h-32 bg-white border-sand rounded-xl" />
              <button type="submit" data-testid="enquiry-submit" disabled={loading} className="w-full rounded-full bg-coral text-white py-4 overline text-xs hover:bg-[#c9633f] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {loading ? "Sending..." : "Send Enquiry"}
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="space-y-5">
              <div className="rounded-3xl overflow-hidden border border-sand h-80" data-testid="service-map">
                <MapContainer center={[-26.6, 153.09]} zoom={9} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; <a href="https://openstreetmap.org/copyright">OpenStreetMap</a> contributors' />
                  <Circle center={[-26.6, 153.09]} radius={38000} pathOptions={{ color: "#E27B58", fillColor: "#E27B58", fillOpacity: 0.12, weight: 2 }} />
                  {spots.map((s) => (
                    <Marker key={s.name} position={s.pos} icon={markerIcon}>
                      <Popup>{s.name}</Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="rounded-2xl bg-seafoam p-5"><MapPin className="w-5 h-5 text-coral mb-3" /><p className="text-sm text-ocean-deep font-medium">Sunshine Coast</p><p className="text-xs text-ocean/70">Noosa to Caloundra</p></div>
                <div className="rounded-2xl bg-seafoam p-5"><Mail className="w-5 h-5 text-coral mb-3" /><p className="text-sm text-ocean-deep font-medium">Email us</p><p className="text-xs text-ocean/70 break-all">BeachsideBartending@gmail.com</p></div>
                <div className="rounded-2xl bg-seafoam p-5"><Clock className="w-5 h-5 text-coral mb-3" /><p className="text-sm text-ocean-deep font-medium">Lead time</p><p className="text-xs text-ocean/70">2+ weeks preferred</p></div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
